import React, { useState } from 'react';
import styled from 'styled-components';
import Footer from './Footer';
import InstantSearch from './InstantSearch';
import LazyPageContent from './LazyPageContent';
import { theme } from '../theme';
import { getImageUrl } from '../utils/imageUtils';

const PageContainer = styled.div<{ $renderContentOnly?: boolean; $fullscreenHeroMobile?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 100vw;
  background: ${theme.colors.white};
  overflow-x: hidden;
  padding-top: ${props => props.$renderContentOnly ? '0' : '88px'}; /* Espace pour le header fixe - réduit */
  min-height: fit-content; /* La hauteur grandit avec le contenu */
  height: auto; /* Pas de hauteur fixe */

  @media (max-width: 768px) {
    padding-top: ${props => props.$fullscreenHeroMobile ? '0' : '106px'}; /* Pas de padding si hero plein écran */
  }

  @media (max-width: 480px) {
    padding-top: ${props => props.$fullscreenHeroMobile ? '0' : '92px'}; /* Pas de padding si hero plein écran */
  }
`;

const HeroSection = styled.div<{ $backgroundImage: string }>`
  position: relative;
  width: 100%;
  height: 400px; /* Hauteur fixe pour que le contenu principal commence toujours à la même position */
  min-height: 400px;
  background: url(${props => props.$backgroundImage}) center/cover;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    height: 160px; /* Hauteur fixe pour que le contenu principal commence toujours à la même position */
    min-height: 160px;
  }

  @media (max-width: 480px) {
    height: 144px; /* Hauteur fixe pour que le contenu principal commence toujours à la même position */
    min-height: 144px;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: radial-gradient(63.99% 192.16% at 82.64% 42%, 
    rgba(54, 52, 55, 0.25) 0%, 
    rgba(34, 33, 35, 0.432543) 0%, 
    rgba(0, 0, 0, 0.49) 100%
  );
`;

const SearchBarContainer = styled.div`
  position: absolute;
  width: 1390px;
  height: 68px;
  left: 50%;
  top: 128px;
  transform: translateX(-50%);
  filter: drop-shadow(0px 1px 4.7px rgba(157, 157, 157, 0.32));
  border-radius: 0px 0px 16px 0px;
  z-index: 2;

  @media (max-width: 1400px) {
    width: calc(100% - 48px);
    max-width: 1390px;
  }

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    height: 56px;
    top: 100px;
  }

  @media (max-width: 480px) {
    width: calc(100% - 24px);
    height: 48px;
    top: 80px;
  }
`;

const SearchBarBackground = styled.div`
  position: absolute;
  left: 0%;
  right: -0.14%;
  top: 0%;
  bottom: 0%;
  background: #FFFFFF;
  border: 1px solid rgba(57, 181, 157, 0.52);
  border-radius: 37px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1.73%;
  right: 95.97%;
  top: 26.47%;
  bottom: 26.47%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    img {
      width: 20px;
      height: 20px;
    }
  }

  @media (max-width: 480px) {
    img {
      width: 18px;
      height: 18px;
    }
  }
`;

const InstantSearchWrapper = styled.div`
  position: absolute;
  left: 5.18%;
  right: 5.18%;
  top: 38.24%;
  bottom: 38.24%;
  z-index: 9999;

  .instant-search-container {
    max-width: none;
    margin: 0;
  }

  .search-input {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
    font-family: ${theme.fonts.body};
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    color: #4A4A4A;
    border-radius: 0;

    &:focus {
      border: none;
      box-shadow: none;
      transform: none;
    }

    &::placeholder {
      color: #4A4A4A;
      opacity: 0.7;
    }
  }

  .search-icon {
    display: none;
  }

  .search-results {
    margin-top: 15px;
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    z-index: 9999 !important;
    background: white !important;
    opacity: 1 !important;
  }

  @media (max-width: 768px) {
    .search-input {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    .search-input {
      font-size: 12px;
    }
  }
`;

const HeroContent = styled.div<{ $isTextBelowTitle?: boolean; $isContact?: boolean }>`
  position: absolute;
  left: 50%;
  top: 180px; /* Position fixe depuis le haut du hero pour que le contenu démarre toujours à la même hauteur - remonté */
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  text-align: center;
  width: 90%;
  max-width: 800px;

  @media (max-width: 768px) {
    gap: ${props => props.$isContact ? '12px' : '20px'};
    width: 85%;
    position: absolute;
    top: ${props => props.$isTextBelowTitle ? '80px' : '20px'}; /* Position en dessous du titre pour les pages spécifiques */
    left: 50%;
    transform: translateX(-50%);
    padding: 0 20px; /* Marges latérales */
  }

  @media (max-width: 480px) {
    gap: ${props => props.$isContact ? '8px' : '16px'};
    width: 85%;
    top: ${props => props.$isTextBelowTitle ? '65px' : '15px'}; /* Position en dessous du titre pour les pages spécifiques */
    padding: 0 15px; /* Marges latérales */
  }
`;

const LogoAndTitle = styled.div<{ $isReliefClimat?: boolean; $isLeHanout?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px; /* Espacement entre le logo et le titre */
  position: absolute;
  top: 28%; /* Descendu sur desktop */
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;

  @media (max-width: 768px) {
    gap: 6px;
    top: 25%;
    ${props => (props.$isReliefClimat || props.$isLeHanout) && `
      flex-wrap: nowrap;
      white-space: nowrap;
    `}
  }

  @media (max-width: 480px) {
    gap: 4px;
    top: 22%;
    ${props => (props.$isReliefClimat || props.$isLeHanout) && `
      flex-wrap: nowrap;
      white-space: nowrap;
    `}
  }
`;

const LogoWhite = styled.div<{ $logoUrl: string }>`
  width: 70px;
  height: 70px;
          background: url(${props => props.$logoUrl}) center/contain no-repeat;
  filter: brightness(0) invert(1);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
  }

  @media (max-width: 480px) {
    width: 42px;
    height: 42px;
  }
`;

const PageTitle = styled.h1<{ $isReliefClimat?: boolean; $isContact?: boolean; $isLeHanout?: boolean }>`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 64px;
  line-height: 72px;
  color: ${theme.colors.white};
  margin: 0;
  white-space: nowrap;

  .mobile-only {
    display: none;
  }

  @media (max-width: 768px) {
    font-size: ${props => {
      if (props.$isReliefClimat) return '32px';
      if (props.$isContact) return '42px';
      return '48px';
    }};
    line-height: ${props => {
      if (props.$isReliefClimat) return '40px';
      if (props.$isContact) return '50px';
      return '56px';
    }};
    white-space: ${props => (props.$isReliefClimat || props.$isLeHanout) ? 'nowrap' : 'normal'};

    .mobile-only {
      display: block;
    }
  }

  @media (max-width: 480px) {
    font-size: ${props => {
      if (props.$isReliefClimat) return '24px';
      if (props.$isContact) return '30px';
      return '36px';
    }};
    line-height: ${props => {
      if (props.$isReliefClimat) return '32px';
      if (props.$isContact) return '38px';
      return '44px';
    }};
    white-space: ${props => (props.$isReliefClimat || props.$isLeHanout) ? 'nowrap' : 'normal'};

    .mobile-only {
      display: block;
    }
  }
`;

const PageDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 20px;
  line-height: 30px;
  color: ${theme.colors.white};
  margin: 0;
  max-width: 1000px;
  text-align: justify;

  .desktop-only {
    display: block;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
    display: block; /* Affiché sur mobile */

    .desktop-only {
      display: none; /* Cacher le retour à la ligne sur mobile */
    }
  }

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 20px;
    display: block; /* Affiché sur mobile */

    .desktop-only {
      display: none; /* Cacher le retour à la ligne sur mobile */
    }
  }
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-family: ${theme.fonts.body};
  font-style: italic;
  font-size: 16px;
  cursor: pointer;
  margin-top: 16px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s ease;
  display: block;
  text-align: center;
  width: 100%;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

// Composant pour le bouton "En savoir plus" mobile
const MobileReadMoreButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-family: ${theme.fonts.body};
  font-style: italic;
  font-size: 16px;
  cursor: pointer;
  margin-top: 16px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s ease;
  display: none; /* Masqué sur desktop */
  text-align: center;
  width: 100%;
  position: relative;
  z-index: 4;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    display: block; /* Visible sur mobile */
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const MobileDescription = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  width: 100%;
  margin-top: 0;
  display: none; /* Masqué sur desktop */
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 3;

  @media (max-width: 768px) {
    display: block; /* Visible sur mobile */
  }
`;

const MobileDescriptionText = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.white};
  margin: 0;
  text-align: justify;
  padding: 12px 16px;
  background: transparent;
  border-radius: 0;
  backdrop-filter: none;

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 18px;
    padding: 10px 14px;
  }
`;

interface SharedPageLayoutProps {
  backgroundImage?: string;
  heroType?: 'search' | 'contact';
  heroTitle?: string | React.ReactNode;
  heroSubtitle?: string | React.ReactNode;
  pageTitle?: string;
  pageDescription?: string;
  children?: React.ReactNode;
  customHeroContent?: React.ReactNode;
  customHeroSection?: React.ReactNode; // Hero section complètement personnalisée (remplace HeroSection)
  enableReadMore?: boolean;
  shortDescription?: string;
  fullDescription?: string;
  renderHeroOnly?: boolean; // Nouveau: rendre seulement le hero (header + hero)
  renderContentOnly?: boolean; // Nouveau: rendre seulement le contenu (sans hero, avec footer)
}

// Component for expandable description with read more functionality
const ExpandableDescription: React.FC<{
  shortDescription: string;
  fullDescription: string;
}> = ({ shortDescription, fullDescription }) => {
  const [showFullText, setShowFullText] = useState(false);

  const handleReadMoreClick = () => {
    setShowFullText(!showFullText);
  };

  return (
    <>
      <PageDescription>
        {shortDescription}{showFullText && (<><br/><br/>{fullDescription.replace(shortDescription, "").trim()}</>)}
      </PageDescription>
      <ReadMoreButton onClick={handleReadMoreClick}>
        {showFullText ? 'En savoir moins' : 'En savoir plus'}
      </ReadMoreButton>
    </>
  );
};

const SharedPageLayout: React.FC<SharedPageLayoutProps> = ({ 
  backgroundImage, 
  heroType = 'search',
  heroTitle,
  heroSubtitle,
  pageTitle,
  pageDescription,
  children,
  customHeroContent,
  customHeroSection,
  enableReadMore = false,
  shortDescription,
  fullDescription,
  renderHeroOnly = false,
  renderContentOnly = false
}) => {
  const renderSearchBar = () => (
    <SearchBarContainer>
      <SearchBarBackground />
      <SearchIcon>
        <img src={getImageUrl("/shared_assets/images/search_loop.png")} alt="Search" />
      </SearchIcon>
      <InstantSearchWrapper>
        <InstantSearch 
          placeholder="Chercher un terme algérien..."
        />
      </InstantSearchWrapper>
    </SearchBarContainer>
  );

  const renderHeroSection = () => {
    // Si un hero section complètement personnalisé est fourni, l'utiliser directement
    if (customHeroSection) {
      return customHeroSection;
    }

    // Identifier les pages où le texte doit être placé en dessous du titre sur mobile
    const currentTitle = pageTitle || heroTitle;
    const pagesWithTextBelowTitle = ['Histoire', 'Relief & Climat', 'Gastronomie', 'Peuples', 'Tradition', 'Le Hanout'];
    const isTextBelowTitle = typeof currentTitle === 'string' && pagesWithTextBelowTitle.includes(currentTitle);
    const isReliefClimat = typeof currentTitle === 'string' && currentTitle === 'Relief & Climat';
    const isLeHanout = typeof currentTitle === 'string' && currentTitle === 'Le Hanout';

    return (
      <HeroSection $backgroundImage={backgroundImage || ''}>
        <HeroOverlay />
        {/* {renderSearchBar()} */}
        {customHeroContent ? (
          customHeroContent
        ) : heroType === 'contact' ? (
          <HeroContent $isContact={true}>
            <PageTitle $isContact={true}>{heroTitle}</PageTitle>
            <PageDescription>{heroSubtitle}</PageDescription>
          </HeroContent>
        ) : (pageTitle || heroTitle) && (
          <>
            {/* Logo + Titre en position fixe par rapport au HeroSection */}
            <LogoAndTitle $isReliefClimat={isReliefClimat} $isLeHanout={isLeHanout}>
              <LogoWhite $logoUrl={getImageUrl('/shared_assets/images/logo_blanc.png')} />
              <PageTitle $isReliefClimat={isReliefClimat} $isLeHanout={isLeHanout}>{pageTitle || heroTitle}</PageTitle>
            </LogoAndTitle>

            {/* Contenu en dessous */}
            <HeroContent $isTextBelowTitle={isTextBelowTitle}>
              {enableReadMore && shortDescription && fullDescription ? (
                <ExpandableDescription 
                  shortDescription={shortDescription}
                  fullDescription={fullDescription}
                />
              ) : (pageDescription || heroSubtitle) && (
                <PageDescription>{pageDescription || heroSubtitle}</PageDescription>
              )}
            </HeroContent>
          </>
        )}
      </HeroSection>
    );
  };

  // Si on veut seulement le hero (header + hero)
  if (renderHeroOnly) {
    return (
      <PageContainer $renderContentOnly={false} $fullscreenHeroMobile={!!customHeroSection}>
        {renderHeroSection()}
      </PageContainer>
    );
  }

  // Si on veut seulement le contenu (sans hero, avec footer)
  if (renderContentOnly) {
    return (
      <PageContainer $renderContentOnly={true} $fullscreenHeroMobile={false}>
        {children}
        <Footer />
      </PageContainer>
    );
  }

  // Rendu complet par défaut - le contenu se charge progressivement
  // Pour la page d'accueil (pageTitle === "Accueil"), charger immédiatement
  const shouldLoadImmediately = pageTitle === "Accueil";
  
  return (
    <PageContainer $renderContentOnly={false} $fullscreenHeroMobile={!!customHeroSection}>
      {renderHeroSection()}
      <LazyPageContent loadImmediately={shouldLoadImmediately}>
        <>
          {children}
          <Footer />
        </>
      </LazyPageContent>
    </PageContainer>
  );
};

export default SharedPageLayout; 